import React from "react";
import { CornerDownRight, ShieldCheck } from "lucide-react";
import {
  OFF_MARKET_SENTENCE_PLAIN,
  OFF_MARKET_SENTENCE_SMS,
  PREFERENCES_SENTENCE_PLAIN,
  PREFERENCES_SENTENCE_SMS,
  previewText,
} from "../utils/messageBlocks";

/**
 * The lines that will be added to the bottom of this message, shown attached to
 * the message box itself.
 *
 * This exists because of a complaint that was entirely about VISIBILITY, and it
 * is worth being precise about why, since the obvious reading is that the tick
 * boxes were broken (CG, Monday 12887883570, 2026-09-01: "I have ticked the
 * invite them to off market portal & update preference buttons but I cant see it
 * added in the text message. Same issue with the email").
 *
 * They were not broken. The blocks were being appended - the character count and
 * the credit quote above the box already included them, and a Preview panel
 * further down the modal rendered the assembled message. But the blocks are
 * deliberately kept OUT of the editable body (each block has exactly one owner,
 * its tick box - that was the 27 Aug fix for ticking a box changing nothing), so
 * the message box shows the agent's own words and nothing else. Christian was
 * looking at the message box, and from there the sentences genuinely were not
 * there. A preview two scrolls below the fold is not an answer to "I can't see
 * it".
 *
 * So the assembled tail now sits directly under the box, greyed out and not
 * editable, reading exactly as it will send. Ticking a box makes a line appear
 * where the message is; unticking it takes it away. Nothing about what gets sent
 * changed.
 */
export default function MessageBlocksTail({
  channel = "email",
  offMarket = false,
  preferences = false,
  // The opt-out line is not optional on either channel, so it is stated rather
  // than ticked.
  unsubscribe = true,
  // The agent put the %%SMS_STOP%% placeholder in the box. It still goes out
  // as the very last line, under these blocks (Monday 13392775822), so the
  // strip says where it ends up instead of dropping the line.
  unsubscribePlaced = false,
  className = "",
}) {
  const isEmail = channel !== "sms";

  const lines = [];
  if (offMarket) {
    lines.push({
      key: "off-market",
      label: "Off Market Portal invitation",
      text: isEmail ? OFF_MARKET_SENTENCE_PLAIN : previewText(OFF_MARKET_SENTENCE_SMS),
    });
  }
  if (preferences) {
    lines.push({
      key: "preferences",
      label: "Preferences link",
      text: isEmail
        ? PREFERENCES_SENTENCE_PLAIN
        : previewText(PREFERENCES_SENTENCE_SMS),
    });
  }

  return (
    <div
      data-testid="message-blocks-tail"
      className={`rounded-b-lg border border-t-0 border-dashed border-gray-300 bg-gray-50 px-3 py-2 space-y-1.5 ${className}`}
    >
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        <CornerDownRight className="w-3 h-3" />
        Added to the end of this {isEmail ? "email" : "text"} when it sends
      </p>

      {lines.map((line) => (
        <p key={line.key} className="text-xs text-gray-600 whitespace-pre-wrap break-words">
          <span className="text-[10px] uppercase tracking-wide text-indigo-400 mr-1.5">
            {line.label}
          </span>
          {line.text}
        </p>
      ))}

      {unsubscribe && (
        <p className="flex items-start gap-1.5 text-xs text-gray-500">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
          {isEmail
            ? "An Unsubscribe button, on its own line at the very bottom."
            : unsubscribePlaced
              ? "Your unsubscribe link, moved to its own line at the very end."
              : "A tap to stop unsubscribe link, on its own line at the very end."}
        </p>
      )}

      {lines.length === 0 && (
        /* Said out loud rather than left blank: an empty strip reads as a bug,
           and "nothing is added" is the answer to the same question. */
        <p className="text-xs text-gray-400">
          Nothing else. Tick a box below to add the Off Market Portal invitation
          or the preferences link.
        </p>
      )}
    </div>
  );
}
